import { useEffect, useRef } from 'react';

export interface ReviewShortcutHandlers {
  /** Approve (the caller confirms first) */
  onApprove?: () => void;
  /** Return for correction (opens the reason prompt) */
  onReturn?: () => void;
  /** Leave the chart review */
  onClose?: () => void;
  /** Show the shortcut list */
  onHelp?: () => void;
}

/** The review shortcuts, for the help dialog */
export const REVIEW_SHORTCUTS: ReadonlyArray<[string, string]> = [
  ['J / K', 'Next / previous entry'],
  ['Enter', 'Open the actions of the focused entry'],
  ['A', 'Approve (asks first)'],
  ['R', 'Return for correction'],
  ['Esc', 'Close the chart'],
  ['?', 'Show these shortcuts'],
];

const typingIn = (el: Element | null): boolean => {
  if (!el) return false;
  const tag = el.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || (el as HTMLElement).isContentEditable;
};

/** Move focus to the next (+1) or previous (-1) chart entry row on the page */
export function moveEntryFocus(step: 1 | -1, root: ParentNode = document): HTMLElement | null {
  const rows = Array.from(root.querySelectorAll<HTMLElement>('[data-entry-id]'));
  if (!rows.length) return null;
  const at = rows.findIndex((r) => r === document.activeElement || r.contains(document.activeElement));
  const next = rows[at < 0 ? (step > 0 ? 0 : rows.length - 1) : Math.min(rows.length - 1, Math.max(0, at + step))];
  next.focus();
  next.scrollIntoView?.({ block: 'nearest' });
  return next;
}

/**
 * Phase 18U U17: keyboard review on desktop. Keys are ignored while typing in a field or while a
 * dialog or sheet is open (those handle their own keys).
 */
export function useReviewShortcuts(enabled: boolean, handlers: ReviewShortcutHandlers): void {
  // Latest handlers through a ref, so the listener is registered once (not after every render,
  // which would put it behind a dialog's own Escape listener)
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;
  useEffect(() => {
    if (!enabled) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.altKey) return;
      const active = document.activeElement;
      if (typingIn(active)) return;
      const handlers = handlersRef.current;
      if (document.querySelector('[role="dialog"], [role="alertdialog"]')) return;
      const key = event.key;
      if (key === 'j' || key === 'J') { event.preventDefault(); moveEntryFocus(1); return; }
      if (key === 'k' || key === 'K') { event.preventDefault(); moveEntryFocus(-1); return; }
      if (key === 'Enter' && active instanceof HTMLElement && active.hasAttribute('data-entry-id')) {
        event.preventDefault();
        active.querySelector<HTMLButtonElement>('button[aria-label^="Actions for"]')?.click();
        return;
      }
      if ((key === 'a' || key === 'A') && handlers.onApprove) { event.preventDefault(); handlers.onApprove(); return; }
      if ((key === 'r' || key === 'R') && handlers.onReturn) { event.preventDefault(); handlers.onReturn(); return; }
      if (key === 'Escape' && handlers.onClose) { event.preventDefault(); handlers.onClose(); return; }
      if (key === '?' && handlers.onHelp) { event.preventDefault(); handlers.onHelp(); }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [enabled]);
}
